import React, { useMemo, useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  MessageSquareQuote,
  FileText,
  ShoppingBag,
  Truck,
  TrendingDown,
  TrendingUp,
  Minus,
} from "lucide-react";
import {
  formatBucketDateRange,
  getChartLayoutConfig,
  getVisibleXAxisTickLabels,
  projectSeriesOntoBuckets,
  selectDisplayBuckets,
  TimelyChartLayoutConfig,
} from "./bucketUtils";
import {
  SalesComparisonMetricId,
  SalesComparisonPeriod,
  SalesComparisonSeries,
  TimelyComparisonBucket,
} from "./types";
import { DashboardSectionDivider, useActivateDashboardSection } from "../DashboardSectionDivider";
import { dashboardInteractiveCardSx } from "../dashboardSurfaceStyles";

interface TimelyComparisonChartsProps {
  period: SalesComparisonPeriod;
  series: SalesComparisonSeries[];
  onCardClick?: (itemId: string) => void;
}

const PERIOD_LABELS: Record<SalesComparisonPeriod, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-Yearly",
  annually: "Annually",
};

const CURRENT_PERIOD_LABELS: Record<SalesComparisonPeriod, string> = {
  weekly: "Current week",
  monthly: "Current month",
  quarterly: "Current quarter",
  half_yearly: "Current half-year",
  annually: "Current year",
};

const METRIC_ICONS: Record<SalesComparisonMetricId, React.ReactNode> = {
  enquiry: <MessageSquareQuote size={14} strokeWidth={2.2} />,
  quotation: <FileText size={14} strokeWidth={2.2} />,
  order: <ShoppingBag size={14} strokeWidth={2.2} />,
  dispatch: <Truck size={14} strokeWidth={2.2} />,
};

const getSeriesStats = (buckets: TimelyComparisonBucket[]) => {
  if (!buckets.length) {
    return { latest: 0, total: 0, changePct: 0 };
  }

  const latest = buckets[buckets.length - 1].value;
  const first = buckets[0].value;
  const total = buckets.reduce((sum, bucket) => sum + bucket.value, 0);
  const changePct =
    first === 0
      ? (latest * 100)
      : Math.round(((latest - first) / first) * 1000) / 10;

  return { latest, total, changePct };
};

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ value?: number; payload?: TimelyComparisonBucket }>;
  color: string;
  title: string;
}> = ({ active, payload, color, title }) => {
  if (!active || !payload?.length || !payload[0].payload) return null;

  const bucket = payload[0].payload;

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        border: "1px solid #e8eaed",
        borderRadius: "10px",
        boxShadow: "0 10px 24px rgba(17,24,39,0.12)",
        px: 1.35,
        py: 1,
        minWidth: 160,
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: "11px",
          fontWeight: 600,
          color: "#111827",
          mb: 0.35,
        }}
      >
        {bucket.label}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: "10px",
          color: "#6b7280",
          mb: 0.75,
        }}
      >
        {formatBucketDateRange(bucket.startDate, bucket.endDate)}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "11px",
            color: "#6b7280",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "12.5px",
            fontWeight: 700,
            color: "#111827",
            ml: "auto",
            pl: 1,
          }}
        >
          {payload[0].value}
        </Typography>
      </Box>
    </Box>
  );
};

const SplineAreaCard: React.FC<{
  series: SalesComparisonSeries;
  displayBuckets: TimelyComparisonBucket[];
  ticks: string[];
  layout: TimelyChartLayoutConfig;
  period: SalesComparisonPeriod;
  onCardClick?: (itemId: string) => void;
}> = ({ series, displayBuckets, ticks, layout, period, onCardClick }) => {
  const stats = useMemo(
    () => getSeriesStats(displayBuckets),
    [displayBuckets],
  );

  const gradientId = `area-fill-${series.id}`;
  const comparison = series.comparison;

  const isUp = comparison
    ? (comparison.trend === "up" || comparison.trend === "from_zero")
    : stats.changePct > 0;
  const isDown = comparison
    ? comparison.trend === "down"
    : stats.changePct < 0;

  const changeColor = isUp ? "#0f9f6e" : isDown ? "#d2363a" : "#6b7280";
  const changeBg = isUp ? "#ecfdf5" : isDown ? "#fff1f1" : "#f3f4f6";
  const displayVal = comparison
    ? comparison.display
    : `${isUp ? "+" : ""}${stats.changePct}%`;

  return (
    <Box
      onClick={() => onCardClick?.(series.id)}
      sx={{
        ...dashboardInteractiveCardSx({
          accentBorder: undefined,
          clickable: Boolean(onCardClick),
        }),
        position: "relative",
        overflow: "hidden",
        minWidth: 0,
        "&:hover": {
          ...dashboardInteractiveCardSx({
            clickable: Boolean(onCardClick),
          })["&:hover"],
          borderColor: `${series.color}55`,
        },
      }}
    >
      <Box
        sx={{
          height: 3,
          background: `linear-gradient(90deg, ${series.color} 0%, ${series.color}33 100%)`,
        }}
      />

      <Box sx={{ p: 2, pb: layout.cardPaddingBottom }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: series.color,
                backgroundColor: series.accentBg,
                flexShrink: 0,
              }}
            >
              {METRIC_ICONS[series.id]}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#111827",
                  lineHeight: 1.25,
                }}
              >
                {series.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "11px",
                  color: "#9ca3af",
                  mt: 0.15,
                }}
              >
                {series.subtitle}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "12px",
                color: "#6b7280",
                fontWeight: 500,
              }}
            >
              {CURRENT_PERIOD_LABELS[period]}:{" "}
              <span style={{ color: "#111827", fontWeight: 700, fontSize: "14px" }}>
                {stats.latest}
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "12px",
                color: "#9ca3af",
                fontWeight: 500,
              }}
            >
              Visible period total:{" "}
              <span style={{ color: "#4b5563", fontWeight: 600, fontSize: "13px" }}>
                {stats.total}
              </span>
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            height: { xs: 280, md: 320 },
            width: "100%",
            borderRadius: "8px",
            background: `linear-gradient(180deg, ${series.accentBg}66 0%, #ffffff 48%)`,
            px: 1,
            overflow: "visible",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={displayBuckets}
              margin={layout.margin}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={series.color} stopOpacity={0.32} />
                  <stop offset="70%" stopColor={series.color} stopOpacity={0.08} />
                  <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#eef2f7"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                ticks={ticks}
                interval={0}
                angle={layout.xAxis.angle}
                textAnchor={layout.xAxis.textAnchor}
                height={layout.xAxis.height}
                padding={{ left: 12, right: 12 }}
                tick={{
                  fontSize: 10.5,
                  fill: "#94a3b8",
                  fontFamily: "Poppins",
                }}
                tickLine={false}
                axisLine={{ stroke: "#e8eaed" }}
                minTickGap={12}
                dy={layout.xAxis.dy}
              />
              <YAxis
                tick={{
                  fontSize: 11,
                  fill: "#94a3b8",
                  fontFamily: "Poppins",
                }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={42}
              />
              <Tooltip
                cursor={{
                  stroke: series.color,
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                  strokeOpacity: 0.45,
                }}
                content={
                  <ChartTooltip color={series.color} title={series.name} />
                }
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={series.color}
                strokeWidth={2.5}
                fill={`url(#${gradientId})`}
                activeDot={{
                  r: 5,
                  stroke: "#ffffff",
                  strokeWidth: 2,
                  fill: series.color,
                }}
                dot={false}
                isAnimationActive
                animationDuration={650}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export const TimelyComparisonCharts: React.FC<TimelyComparisonChartsProps> = ({
  period,
  series,
  onCardClick,
}) => {
  const { activateSection } = useActivateDashboardSection();
  const charts = series;
  const chartSectionRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const el = chartSectionRef.current;
    if (!el) return;

    const update = () => setChartWidth(el.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const referenceBuckets = charts[0]?.buckets ?? [];

  const displayTemplate = useMemo(
    () => selectDisplayBuckets(referenceBuckets, chartWidth, period),
    [referenceBuckets, chartWidth, period],
  );

  const ticks = useMemo(
    () => getVisibleXAxisTickLabels(displayTemplate, chartWidth, period),
    [displayTemplate, chartWidth, period],
  );

  const layout = useMemo(
    () => getChartLayoutConfig(displayTemplate.length),
    [displayTemplate.length],
  );

  const alignedCharts = useMemo(
    () =>
      charts.map((chartSeries) => ({
        series: chartSeries,
        displayBuckets: projectSeriesOntoBuckets(
          chartSeries.buckets,
          displayTemplate,
        ),
      })),
    [charts, displayTemplate],
  );

  return (
    <Box sx={{ mt: 2.5 }} onClickCapture={() => activateSection("timely")}>
      <DashboardSectionDivider sectionId="timely" />
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "15px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            Sales Trend Charts
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "11px",
              color: "#6b7280",
              mt: 0.15,
            }}
          >
            Enquiry, Quotation, Order, and Dispatch charts by historical bucket
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            maxWidth: { xs: "100%", md: "50%" },
            justifyContent: "flex-end",
          }}
        >
          {charts.map((series) => (
            <Box
              key={series.id}
              sx={{
                px: 0.75,
                py: 0.3,
                borderRadius: "999px",
                border: "1px solid #e5e7eb",
                backgroundColor: series.accentBg,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "10px",
                  fontWeight: 600,
                  color: series.color,
                }}
              >
                {series.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.35,
            borderRadius: "999px",
            border: "1px solid #e5e7eb",
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "10.5px",
              fontWeight: 600,
              color: "#4b5563",
            }}
          >
            {PERIOD_LABELS[period]}
          </Typography>
        </Box>
      </Box>

      <Box
        ref={chartSectionRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        {alignedCharts.map(({ series: chartSeries, displayBuckets }) => (
          <SplineAreaCard
            key={chartSeries.id}
            series={chartSeries}
            displayBuckets={displayBuckets}
            ticks={ticks}
            layout={layout}
            period={period}
            onCardClick={onCardClick}
          />
        ))}
      </Box>
    </Box>
  );
};
